import { flow, filter, get, map, pick, isEmpty, toPairs, mapValues } from 'lodash/fp';
import recommendations from '../choices/recommendations';
import fullName, { fullNamePartner } from './fullName';

export const filterRecommendationsCIF = (recommendationsToFilter) => flow([
    toPairs,
    // retourne les reco CIF dont le statut d'enveloppe est en création ou composition ou non défini pour que le chargé d'affaires puisse relancer le cif meme lorsque celui ci est approuvé
    filter(([, { statusEnvelope }]) =>
        !statusEnvelope ||
        'Envelope_Signature_EN_CREATION' === statusEnvelope ||
        'Envelope_Signature_COMPOSITION' === statusEnvelope),
    map(([type, { data }]) => ({ ...data, type })),
])(recommendationsToFilter);

export const filterRecommendations = (recommendationsToFilter, approveState = false) => flow([
    toPairs,
    // ensure it
    filter(([, { isApproved }]) => !!isApproved === approveState),
    map(([type, { data }]) => ({ ...data, type })),
])(recommendationsToFilter);

export const hasApprovalFor = (type) => {
    const allowingRecommendations = flow([
        toPairs,
        filter(([, { simulator }]) => type === simulator),
        map(get(0)),
    ])(recommendations);

    const hasApproval = flow([
        get('recommendations'),
        pick(allowingRecommendations),
        filter(({ isApproved }) => isApproved),
        (value) => !isEmpty(value),
    ]);

    return (rip, target = 'client', { canCreateSimulation, isEliteCGP, needApprovalOnSimulations }) => {
        if (!canCreateSimulation && !isEliteCGP) {
            return false;
        }

        if (!needApprovalOnSimulations) {
            // some users don't need approvals to create simulation
            return true;
        }

        if ('partner' === target && 'cohabitation' === rip.familySituation && rip.dualRip) {
            return hasApproval(get('partner', rip));
        }

        return hasApproval(rip);
    };
};

const collectionHasApprovedRecommendations = (collection) => !isEmpty(collection)
    && collection.some(({ isApproved }) => isApproved);

export const hasApprovedRecommendations = (rip) => collectionHasApprovedRecommendations(get('recommendations', rip))
    || (
        'cohabitation' === rip.familySituation
        && rip.dualRip
        && collectionHasApprovedRecommendations(get('partner.recommendations', rip))
    );

export const toApprovedRecommendations = (recommendationsMap) =>
    mapValues((rec) => ({ ...rec, isApproved: true }), recommendationsMap);

const selectAdvisorEmailRecommendations = (rip, { isCifScpi = false, target } = {}) => {
    if (!isCifScpi) {
        const isCohabitation = 'cohabitation' === rip.familySituation && rip.dualRip;

        return [
            ...filterRecommendations(rip.recommendations)
                .map((item) => ({ ...item, target: rip.firstName })),
            ...(isCohabitation ? filterRecommendations(rip.partner && rip.partner.recommendations)
                .map((item) => ({ ...item, target: rip.partnerFirstName })) : []),
        ];
    }

    return [
        ...('clientAndPartner' === target ? filterRecommendations(rip.recommendationsCIFClientAndPartner)
            .map((item) => ({ ...item, target: `${rip.firstName} et ${rip.partnerFirstName}` })) : []),
        ...('client' === target ? filterRecommendations(rip.recommendationsCIF)
            .map((item) => ({ ...item, target: rip.firstName })) : []),
        ...('partner' === target ? filterRecommendations(rip.partner && rip.partner.recommendationsCIF)
            .map((item) => ({ ...item, target: rip.partnerFirstName })) : []),
    ];
};

export default selectAdvisorEmailRecommendations;

/**
 * Récupère le nom du contact en fonction de la cible (client, partenaire ou les deux)
 * pour l'affichage dans l'email de notification.
 *
 * @param {Object} rip - Le RIP contenant les informations du client et du partenaire.
 * @param {string} cible - La cible de la recommandation ('client', 'partner', 'clientAndPartner').
 * @returns {string} - Le nom complet du contact correspondant à la cible.
 */
export const getNameByCible = (rip, cible) => {
    const client = fullName(rip);
    const partner = fullNamePartner(rip);

    if ('clientAndPartner' === cible) {
        return `${client} et ${partner}`;
    }
    if ('partner' === cible) {
        return partner;
    }

    return client;
};
